import {
  Input,
  Popover,
  PopoverContent,
  PopoverHandler,
} from "@material-tailwind/react";
import {
  format,
  resolveBrowserLocale,
} from "../../../modules/shared/utils/date";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";

export interface DatePickerProps {
  label?: string;
  value?: Date;
  onChange?: (value: Date) => void;
  required?: boolean;
  error?: boolean;
  showTime?: boolean;
}

function formatTime(date: Date): string {
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function mergeDateAndTime(date: Date, timeString: string): Date {
  const [hours, minutes] = timeString.split(":").map(Number);
  const merged = new Date(date);
  merged.setHours(hours ?? 0, minutes ?? 0, 0, 0);
  return merged;
}

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      label,
      value: defaultValue,
      onChange,
      required,
      error,
      showTime,
    }: DatePickerProps,
    ref
  ) => {
    const [value, setValue] = useState<Date | undefined>(defaultValue);
    const [timeString, setTimeString] = useState<string>(
      defaultValue && showTime ? formatTime(defaultValue) : "00:00"
    );
    const inputRef = useRef<HTMLInputElement>(null);
    const locale = useMemo(() => resolveBrowserLocale(), []);

    useEffect(() => {
      setValue(defaultValue);
      if (defaultValue && showTime) {
        setTimeString(formatTime(defaultValue));
      }
      if (inputRef.current) {
        inputRef.current.value = defaultValue
          ? format(defaultValue, "PPP", { locale })
          : "";
      }
    }, [defaultValue, showTime, locale]);

    const handleDaySelect = (selected: Date | undefined) => {
      if (!selected) return;
      const merged = showTime
        ? mergeDateAndTime(selected, timeString)
        : selected;
      setValue(merged);
      if (inputRef.current) {
        inputRef.current.value = format(merged, "PPP", { locale });
        inputRef.current.focus();
      }
      if (onChange) {
        onChange(merged);
      }
    };

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTime = e.target.value;
      setTimeString(newTime);
      if (value) {
        const merged = mergeDateAndTime(value, newTime);
        setValue(merged);
        if (onChange) {
          onChange(merged);
        }
      }
    };

    return (
      <div className="flex flex-col gap-2">
        <Popover placement="bottom">
          <PopoverHandler>
            <Input
              label={label}
              required={required}
              error={error}
              ref={ref}
              inputRef={inputRef}
              value={value ? format(value, "PPP", { locale }) : ""}
              onChange={() => {}}
            />
          </PopoverHandler>
          <PopoverContent>
            <DayPicker
              mode="single"
              selected={value}
              onSelect={handleDaySelect}
              classNames={{
                caption: "flex justify-center py-2 mb-4 relative items-center",
                caption_label: "text-sm font-medium text-gray-900",
                table: "w-full border-collapse",
                head_row: "flex font-medium text-gray-900",
                head_cell: "m-0.5 w-9 font-normal text-sm",
                row: "flex w-full mt-2",
                cell: "text-gray-600 rounded-md h-9 w-9 text-center text-sm p-0 m-0.5 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-gray-900/20 [&:has([aria-selected].day-outside)]:text-white [&:has([aria-selected])]:bg-gray-900/50 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                day: "h-9 w-9 p-0 font-normal",
                day_range_end: "day-range-end",
                day_selected:
                  "rounded-md bg-gray-900 text-white hover:bg-gray-900 hover:text-white focus:bg-gray-900 focus:text-white",
                day_today: "rounded-md bg-gray-200 text-gray-900",
                day_outside:
                  "day-outside text-gray-500 opacity-50 aria-selected:bg-gray-500 aria-selected:text-gray-900 aria-selected:bg-opacity-10",
                day_disabled: "text-gray-500 opacity-50",
                day_hidden: "invisible",
              }}
              required
            />
          </PopoverContent>
        </Popover>
        {showTime && (
          <Input
            type="time"
            label="Hora"
            value={timeString}
            onChange={handleTimeChange}
          />
        )}
      </div>
    );
  }
);
